import { useEffect, useRef, useState } from "react";
import { prettifyApiError } from "@/shared/api";
import {
  bozzaPronta,
  chiediAgente,
  statoAgente,
  type BozzaPronta,
  type StatoAgente,
  type Turno,
} from "../agente";
import { RegoleAgente } from "./RegoleAgente";

/** La domanda del tasto rapido: la cosa che serve nove volte su dieci. */
export const DOMANDA_BOZZA = "Leggi la conversazione e scrivi la bozza di risposta al cliente.";

/**
 * Pannello «Chiedi a Claude» sopra la casella di risposta.
 *
 * La bozza proposta entra da sola nella casella solo se e' vuota: il testo
 * che l'operatore ha gia' scritto non si schiaccia mai. Altrimenti resta qui
 * con «Usa questa bozza» (il riquadro sparisce quando e' gia' nella casella). La chat vive solo finche' la conversazione e'
 * aperta (il pannello e' montato per thread): ogni domanda manda la storia.
 */
export function AgentePanel({
  threadId,
  testoCasella,
  onBozza,
  onChiudi,
}: {
  threadId: number;
  /** Il testo della casella di risposta, com'e' adesso. */
  testoCasella: string;
  onBozza: (testo: string) => void;
  onChiudi: () => void;
}) {
  const [storia, setStoria] = useState<Turno[]>([]);
  const [bozza, setBozza] = useState<string | null>(null);
  const [domanda, setDomanda] = useState("");
  const [inCorso, setInCorso] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  const [stato, setStato] = useState<StatoAgente | null>(null);
  // Bozza preparata all'arrivo del messaggio: si mostra, non entra mai da sola.
  const [pronta, setPronta] = useState<BozzaPronta | null>(null);
  const [regoleAperte, setRegoleAperte] = useState(false);
  const vivoRef = useRef(true);

  useEffect(() => {
    vivoRef.current = true;
    void statoAgente()
      .then((s) => vivoRef.current && setStato(s))
      .catch(() => undefined);
    void bozzaPronta(threadId)
      .then((b) => vivoRef.current && setPronta(b))
      .catch(() => undefined);
    return () => {
      vivoRef.current = false;
    };
  }, [threadId]);

  async function chiedi(testo: string) {
    const pulita = testo.trim();
    if (!pulita || inCorso) return;
    setInCorso(true);
    setErrore(null);
    try {
      const risposta = await chiediAgente(threadId, pulita, storia);
      if (!vivoRef.current) return;
      setStoria((prima) => [
        ...prima,
        { ruolo: "operatore", testo: pulita },
        { ruolo: "claude", testo: risposta.testo || "(nessun testo)" },
      ]);
      setDomanda("");
      if (risposta.bozza) {
        setBozza(risposta.bozza);
        if (!testoCasella.trim()) onBozza(risposta.bozza);
      } else if (risposta.bozza_motivo) {
        setErrore(`Bozza scartata da OM: ${risposta.bozza_motivo}`);
      }
      void statoAgente()
        .then((s) => vivoRef.current && setStato(s))
        .catch(() => undefined);
    } catch (err) {
      if (vivoRef.current) setErrore(prettifyApiError(err));
    } finally {
      if (vivoRef.current) setInCorso(false);
    }
  }

  return (
    <section
      aria-label="Chiedi a Claude"
      className="mb-2 rounded-2xl border border-line-2 bg-paper px-3.5 py-3 text-[13px] text-ink"
    >
      <div className="flex items-center justify-between gap-2">
        <p className="font-semibold">Chiedi a Claude</p>
        <div className="flex items-center gap-2">
          {stato && (
            <span className="text-[12px] tabular-nums" title={`Modello ${stato.modello}`}>
              Spesa del mese {stato.spesa_mese_usd} $ su {stato.tetto_mese_usd} $
            </span>
          )}
          <button
            type="button"
            onClick={onChiudi}
            aria-label="Chiudi Claude"
            className="grid h-7 w-7 place-items-center rounded-full hover:bg-card"
          >
            <svg
              className="h-3.5 w-3.5 fill-none stroke-current stroke-[1.9] [stroke-linecap:round]"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>
      </div>

      {storia.length > 0 && (
        <ol className="mt-2 max-h-[220px] space-y-2 overflow-y-auto" aria-label="Chat con Claude">
          {storia.map((turno, indice) => (
            <li
              key={indice}
              className={`whitespace-pre-wrap rounded-xl px-3 py-2 ${
                turno.ruolo === "claude" ? "bg-card" : "bg-line/40 font-semibold"
              }`}
            >
              {turno.testo}
            </li>
          ))}
        </ol>
      )}

      {!bozza && pronta && pronta.testo.trim() !== testoCasella.trim() && (
        <div className="mt-2 rounded-xl border border-line-2 bg-card px-3 py-2">
          <p className="text-[12px] font-semibold">Bozza pronta di Claude</p>
          <p className="text-[11.5px]">
            Scritta all&apos;arrivo del messaggio
            {pronta.tipo ? ` · tipo: ${pronta.tipo}` : ""}. Controllala prima di inviare.
          </p>
          <p className="mt-1 whitespace-pre-wrap" data-testid="bozza-pronta">
            {pronta.testo}
          </p>
          <button
            type="button"
            onClick={() => onBozza(pronta.testo)}
            className="mt-2 h-8 rounded-full border border-ink px-3 text-[12.5px] font-semibold hover:bg-ink hover:text-on-ink"
          >
            Usa questa bozza
          </button>
        </div>
      )}

      {bozza && bozza.trim() !== testoCasella.trim() && (
        <div className="mt-2 rounded-xl border border-line-2 bg-card px-3 py-2">
          <p className="text-[12px] font-semibold">Bozza proposta</p>
          <p className="mt-1 whitespace-pre-wrap" data-testid="bozza-claude">
            {bozza}
          </p>
          <button
            type="button"
            onClick={() => onBozza(bozza)}
            className="mt-2 h-8 rounded-full border border-ink px-3 text-[12.5px] font-semibold hover:bg-ink hover:text-on-ink"
          >
            Usa questa bozza
          </button>
        </div>
      )}

      {errore && (
        <p role="alert" className="mt-2 rounded-xl bg-red-soft px-3 py-2 text-[12.5px]">
          {errore}
        </p>
      )}

      <form
        className="mt-2 flex flex-wrap items-center gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          void chiedi(domanda);
        }}
      >
        <button
          type="button"
          onClick={() => void chiedi(DOMANDA_BOZZA)}
          disabled={inCorso}
          className="h-9 shrink-0 rounded-full border border-ink bg-ink px-4 text-[12.5px] font-semibold text-on-ink disabled:opacity-45"
        >
          {inCorso ? "Claude legge…" : "Scrivi la bozza"}
        </button>
        <input
          value={domanda}
          onChange={(event) => setDomanda(event.target.value)}
          disabled={inCorso}
          maxLength={4000}
          placeholder="Oppure chiedi: dov'è il pacco? cosa ha ordinato?"
          aria-label="Domanda a Claude"
          className="h-9 min-w-[180px] flex-1 rounded-full border border-line-2 bg-card px-3.5 text-[13px] outline-none focus:border-ink"
        />
        <button
          type="submit"
          disabled={inCorso || !domanda.trim()}
          className="h-9 shrink-0 rounded-full border border-line-2 bg-card px-3.5 text-[12.5px] font-semibold disabled:opacity-45"
        >
          Chiedi
        </button>
      </form>
      <p className="mt-1.5 text-[11.5px]">
        Claude legge e propone. Non invia niente: l&apos;invio lo fai tu.
      </p>
      {stato && <RigaBozze conti={stato.bozze_30_giorni} />}
      <button
        type="button"
        onClick={() => setRegoleAperte((aperte) => !aperte)}
        aria-expanded={regoleAperte}
        className="mt-1.5 text-[12px] font-semibold underline"
      >
        {regoleAperte ? "Nascondi le regole di Kery" : "Regole di Kery"}
      </button>
      {regoleAperte && <RegoleAgente />}
    </section>
  );
}

/** Quante bozze di Claude sono partite cosi' com'erano: la misura dell'agente. */
function RigaBozze({ conti }: { conti: Record<string, number> }) {
  const uguali = conti.uguale ?? 0;
  const ritoccate = conti.ritoccata ?? 0;
  const riscritte = conti.riscritta ?? 0;
  if (uguali + ritoccate + riscritte === 0) return null;
  return (
    <p className="mt-1 text-[11.5px] tabular-nums" data-testid="bozze-30-giorni">
      Ultimi 30 giorni: {uguali} bozze inviate uguali, {ritoccate} ritoccate, {riscritte} riscritte.
    </p>
  );
}
